/**
 * Achievements Engine
 *
 * Pure functions — no side effects, no React imports.
 *
 * Primary API:
 *   checkAchievements(progress)
 *     → string[]   IDs of achievements whose conditions are now met
 *
 *   getAchievementProgress(progress)
 *     → { [achievementId]: { current, max, pct } }
 *       Numeric progress toward every achievement that has a progressMax,
 *       used to render near-miss progress bars.
 */

import achievementsData from '../game/achievements.json';
import levelsData       from '../game/levels.json';

// ─── helpers ────────────────────────────────────────────────────────────────

const totalLevels = levelsData.levels.length; // 10

/** Return the difficulty of a level by id (1-indexed). */
const levelDifficulty = (levelId) => {
  const lvl = levelsData.levels.find((l) => l.id === levelId);
  return lvl ? lvl.difficulty : null;
};

/** Maximum score found across ALL attempt histories. */
const bestScoreEver = (progress) => {
  return Math.max(0, ...Object.values(progress.highScores || {}));
};

/** How many levels have at least `minStars` stars. */
const countLevelsWithStars = (progress, minStars) => {
  return Object.values(progress.levelStars || {}).filter((s) => s >= minStars).length;
};

/** Maximum attempts recorded for a single level. */
const maxAttemptsOnOneLevel = (progress) => {
  return Math.max(
    0,
    ...Object.values(progress.levelHistory || {}).map((h) => h.length)
  );
};

/**
 * Best improvement on a single level between any two consecutive attempts.
 * "Comeback" achievement: improved by ≥ 20 pts in one retry.
 */
const bestSingleRetryImprovement = (progress) => {
  let best = 0;
  for (const history of Object.values(progress.levelHistory || {})) {
    for (let i = 1; i < history.length; i++) {
      const improvement = history[i].score - history[i - 1].score;
      if (improvement > best) best = improvement;
    }
  }
  return best;
};

/** Highest level ID that is unlocked. */
const maxUnlockedLevel = (progress) => {
  return Math.max(0, ...(progress.unlockedLevels || [1]));
};

/** True if the user has a stored calibration value (any non-null/non-zero). */
const hasCalibration = (progress) => {
  return !!(
    progress.userSaFrequency ||
    progress.user_sa_frequency ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('userSaFrequency'))
  );
};

// ─── condition checkers ─────────────────────────────────────────────────────
// Each returns true if the achievement's condition is satisfied.

const CONDITIONS = {
  // First Steps
  first_note:        (p) => (p.completedLevels || []).includes(1),
  first_pass:        (p) => (p.completedLevels || []).length >= 1,
  first_calibration: (p) => hasCalibration(p),
  first_three_star:  (p) => Object.values(p.levelStars || {}).some((s) => s >= 3),
  first_five_star:   (p) => Object.values(p.levelStars || {}).some((s) => s >= 5),

  // Streak
  streak_3:  (p) => (p.streakData?.longestStreak || 0) >= 3,
  streak_7:  (p) => (p.streakData?.longestStreak || 0) >= 7,
  streak_14: (p) => (p.streakData?.longestStreak || 0) >= 14,
  streak_30: (p) => (p.streakData?.longestStreak || 0) >= 30,

  // Precision
  score_90:          (p) => bestScoreEver(p) >= 90,
  score_95:          (p) => bestScoreEver(p) >= 95,
  score_100:         (p) => bestScoreEver(p) >= 100,
  five_star_hard:    (p) =>
    Object.entries(p.levelStars || {}).some(
      ([id, s]) => s >= 5 && ['hard', 'expert'].includes(levelDifficulty(Number(id)))
    ),
  five_star_all_easy:(p) => {
    const easyLevels = levelsData.levels.filter((l) => l.difficulty === 'easy').map((l) => l.id);
    return easyLevels.every((id) => (p.levelStars || {})[id] >= 5);
  },
  perfect_descent:   (p) =>
    [(p.highScores || {})[5], (p.highScores || {})[7]].some((s) => s != null && s >= 85),

  // Explorer
  unlock_5:      (p) => maxUnlockedLevel(p) >= 5,
  unlock_10:     (p) => maxUnlockedLevel(p) >= 10,
  complete_5:    (p) => (p.completedLevels || []).filter((id) => id <= 5).length >= 5,
  complete_all:  (p) => (p.completedLevels || []).length >= totalLevels,
  dha_reached:   (p) => (p.completedLevels || []).includes(8),
  speed_run_pass:(p) => (p.completedLevels || []).includes(9),
  octave_complete:(p)=> (p.completedLevels || []).includes(10),

  // Persistence
  attempts_10:     (p) => (p.totalAttempts || 0) >= 10,
  attempts_50:     (p) => (p.totalAttempts || 0) >= 50,
  attempts_100:    (p) => (p.totalAttempts || 0) >= 100,
  retry_champion:  (p) => maxAttemptsOnOneLevel(p) >= 5,
  comeback:        (p) => bestSingleRetryImprovement(p) >= 20,

  // Completionist
  three_star_all:  (p) => countLevelsWithStars(p, 3) >= totalLevels,
  four_star_all:   (p) => countLevelsWithStars(p, 4) >= totalLevels,
  five_star_all:   (p) => countLevelsWithStars(p, 5) >= totalLevels,
  total_stars_25:  (p) =>
    Object.values(p.levelStars || {}).reduce((s, v) => s + v, 0) >= 25,
  total_stars_50:  (p) =>
    Object.values(p.levelStars || {}).reduce((s, v) => s + v, 0) >= 50,
};

// ─── public API ─────────────────────────────────────────────────────────────

/**
 * Given the current progress object, return an array of achievement IDs
 * whose conditions are now met.
 *
 * The caller compares this against already-unlocked IDs to find *newly*
 * unlocked ones.
 */
export const getAllMetAchievements = (progress) => {
  return achievementsData.achievements
    .map((a) => a.id)
    .filter((id) => {
      const fn = CONDITIONS[id];
      if (!fn) return false;
      try { return fn(progress); } catch (_) { return false; }
    });
};

/**
 * Check progress against previously-unlocked achievements and return
 * only the IDs that are *newly* unlocked this call.
 *
 * @param {Object} progress          - Full progress object from GameContext
 * @param {string[]} alreadyUnlocked - IDs that were unlocked before this check
 * @returns {string[]}               - Newly unlocked IDs (may be empty)
 */
export const checkAchievements = (progress, alreadyUnlocked = []) => {
  const alreadySet = new Set(alreadyUnlocked);
  return getAllMetAchievements(progress).filter((id) => !alreadySet.has(id));
};

/**
 * Return numeric progress toward every achievement that has a progressMax.
 * Used for near-miss progress bars on the Achievements page.
 *
 * @returns {{ [id]: { current: number, max: number, pct: number } }}
 */
export const getAchievementProgress = (progress) => {
  const totalStars = Object.values(progress.levelStars || {}).reduce((s, v) => s + v, 0);
  const easyLevels = levelsData.levels.filter((l) => l.difficulty === 'easy').map((l) => l.id);

  const progressors = {
    currentStreak:    (p) => p.streakData?.currentStreak || 0,
    bestScore:        (p) => bestScoreEver(p),
    maxUnlocked:      (p) => maxUnlockedLevel(p),
    completedCount:   (p) => (p.completedLevels || []).length,
    totalAttempts:    (p) => p.totalAttempts || 0,
    maxLevelAttempts: (p) => maxAttemptsOnOneLevel(p),
    totalStars:       (_) => totalStars,
    threeStarCount:   (p) => countLevelsWithStars(p, 3),
    fourStarCount:    (p) => countLevelsWithStars(p, 4),
    fiveStarCount:    (p) => countLevelsWithStars(p, 5),
    easyFiveStars:    (p) => easyLevels.filter((id) => (p.levelStars || {})[id] >= 5).length,
  };

  const result = {};
  for (const achievement of achievementsData.achievements) {
    const { id, progressMax, progressKey } = achievement;
    if (!progressMax || !progressKey) continue;
    const getter = progressors[progressKey];
    if (!getter) continue;
    const current = getter(progress);
    result[id] = {
      current: Math.min(current, progressMax),
      max: progressMax,
      pct: Math.min(Math.round((current / progressMax) * 100), 100),
    };
  }
  return result;
};

/** Convenience: look up a single achievement definition by id. */
export const getAchievementById = (id) =>
  achievementsData.achievements.find((a) => a.id === id) || null;
